import type { VoteBatchItemIn } from "@/api/openapi/discovery/types.gen";
import type { TopicId } from "@/stores/priorityStore";
import { buildVoteChanges, type VoteSnapshot } from "./voteChanges";

type SnapshotSender = (votes: VoteBatchItemIn[]) => Promise<void>;
type SubmissionVotesBuilder = (snapshot: VoteSnapshot) => VoteBatchItemIn[];

type SyncState = {
  acknowledged: VoteSnapshot | null;
  desired: VoteSnapshot | null;
  queue: Promise<void>;
};

const syncStates = new Map<string, SyncState>();

function cloneSnapshot(snapshot: VoteSnapshot): VoteSnapshot {
  return {
    ids: [...snapshot.ids],
    comments: { ...snapshot.comments },
  };
}

function stateFor(key: string): SyncState {
  const existing = syncStates.get(key);
  if (existing) return existing;
  const created: SyncState = {
    acknowledged: null,
    desired: null,
    queue: Promise.resolve(),
  };
  syncStates.set(key, created);
  return created;
}

export function participationVoteSyncKey(params: {
  token: string;
  accessMode: "anonymous" | "authenticated";
  identityKey: string;
}): string {
  return `${params.token}:${params.accessMode}:${params.identityKey}`;
}

export function acknowledgeParticipationVotes(key: string, snapshot: VoteSnapshot): void {
  const state = stateFor(key);
  const acknowledged = cloneSnapshot(snapshot);
  state.acknowledged = acknowledged;
  state.desired = acknowledged;
}

export function hasAcknowledgedParticipationVotes(key: string): boolean {
  return stateFor(key).acknowledged !== null;
}

export function clearParticipationVoteSync(key: string): void {
  syncStates.delete(key);
}

export function queueParticipationVoteAutosave(
  key: string,
  desired: VoteSnapshot,
  sendVotes: SnapshotSender,
): Promise<void> {
  const state = stateFor(key);
  state.desired = cloneSnapshot(desired);
  const request = state.queue
    .catch(() => undefined)
    .then(async () => {
      if (!state.acknowledged || !state.desired) return;
      const snapshotForRequest = cloneSnapshot(state.desired);
      const changes = buildVoteChanges(state.acknowledged, snapshotForRequest);
      if (changes.length === 0) return;
      await sendVotes(changes);
      state.acknowledged = snapshotForRequest;
    });
  state.queue = request;
  return request;
}

export function queueParticipationVoteSubmission(
  key: string,
  desired: VoteSnapshot,
  buildVotes: SubmissionVotesBuilder,
  sendVotes: SnapshotSender,
): Promise<void> {
  const state = stateFor(key);
  state.desired = cloneSnapshot(desired);
  const request = state.queue
    .catch(() => undefined)
    .then(async () => {
      const snapshotForRequest = cloneSnapshot(state.desired ?? desired);
      const votes = buildVotes(snapshotForRequest);
      if (votes.length > 0) {
        await sendVotes(votes);
      }
      state.acknowledged = snapshotForRequest;
    });
  state.queue = request;
  return request;
}

export function snapshotFromSelection(ids: TopicId[], comments: Record<TopicId, string>): VoteSnapshot {
  const selected = new Set(ids);
  return {
    ids: [...ids],
    comments: Object.fromEntries(Object.entries(comments).filter(([id]) => selected.has(id))) as Record<
      TopicId,
      string
    >,
  };
}
